import { IDENTITY_COLORS } from "@manifold/protocol";
import { instanceOrigin } from "@manifold/plugin/instance";
import { Cover } from "@manifold/ui";
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import {
  createPrincipal,
  getPreviewIdentityAuthority,
  issuePreviewIdentity,
  startPreviewIdentity,
} from "./api.ts";
import { onIdentityRejected } from "./http.ts";
import {
  IDENTITY_STORAGE,
  credentialKey,
  identityExpired,
  loadIdentity,
  type StoredIdentity,
} from "./identity-storage.ts";

const OWNER_KEY_STORAGE = "manifold.ownerKey";
const OWNER_KEY_PATTERN = /^[0-9a-f]{64}$/i;
const OWNER_FRAGMENT_PATTERN = /^#key=([0-9a-f]{64})$/i;
const PREVIEW_NONCE_STORAGE = "manifold.previewNonce";
let bootstrapOwnerKey: {
  readonly storageKey: string;
  readonly value: string;
  readonly explicit: boolean;
} | null = null;

function previewHandoffRequest(): { audience: string; nonce: string } | null {
  if (window.location.pathname !== "/auth/preview") return null;
  const params = new URLSearchParams(window.location.search);
  const audience = params.get("audience");
  const nonce = params.get("nonce");
  if (audience === null || nonce === null) return null;
  return { audience, nonce };
}

function MissingIdentity({ message }: { readonly message: string }) {
  return (
    <main className="gate-screen">
      <Cover className="gate-cover">
        <section className="gate-card" aria-labelledby="owner-link-title">
          <p className="eyebrow">manifold</p>
          <h1 id="owner-link-title">Sign in required</h1>
          <p>{message}</p>
        </section>
      </Cover>
    </main>
  );
}

function PreviewAdmission() {
  const [message, setMessage] = useState("Checking production identity…");
  useEffect(() => {
    let active = true;
    void getPreviewIdentityAuthority()
      .then(async (authority) => {
        if (!active) return;
        if (authority === null) {
          setMessage(
            "This browser has no owner key or identity token. Start manifold and open its full pre-authenticated URL to continue.",
          );
          return;
        }
        const nonce = await startPreviewIdentity();
        if (!active) return;
        sessionStorage.setItem(PREVIEW_NONCE_STORAGE, nonce);
        const target = new URL("/auth/preview", authority);
        target.searchParams.set("audience", window.location.origin);
        target.searchParams.set("nonce", nonce);
        window.location.assign(target);
      })
      .catch((reason: unknown) => {
        if (active) {
          setMessage(
            reason instanceof Error
              ? reason.message
              : "Production identity could not be reached. Try again.",
          );
        }
      });
    return () => {
      active = false;
    };
  }, []);
  return <MissingIdentity message={message} />;
}

function PreviewHandoff({
  identity,
  request,
}: {
  readonly identity: StoredIdentity;
  readonly request: { audience: string; nonce: string };
}) {
  const [message, setMessage] = useState("Authorizing preview…");
  useEffect(() => {
    let active = true;
    void issuePreviewIdentity(identity.token, request.audience, request.nonce)
      .then((assertion) => {
        if (!active) return;
        const form = document.createElement("form");
        form.method = "POST";
        form.action = `${request.audience}/auth/preview/callback`;
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "assertion";
        input.value = assertion;
        form.append(input);
        document.body.append(form);
        form.submit();
      })
      .catch((reason: unknown) => {
        if (!active) return;
        const detail =
          reason instanceof Error
            ? reason.message
            : "This production identity cannot open the preview.";
        setMessage(detail);
      });
    return () => {
      active = false;
    };
  }, [identity.token, request.audience, request.nonce]);
  return <MissingIdentity message={message} />;
}

/**
 * The one color scheme: principals pick from it, machine dots hash into it. It lives in the
 * protocol now, because the server derives `MachineSummary.color` from the same palette and
 * two ends agreeing on a list of colors makes it vocabulary rather than styling.
 */
export { IDENTITY_COLORS };

/** Captures bootstrap authority in memory and retires legacy recovery-key storage before render. */
export function captureOwnerKeyFromFragment(): void {
  const storageKey = credentialKey(OWNER_KEY_STORAGE);
  const legacyKey = window.localStorage.getItem(storageKey);
  // Storage key order may change when a key is removed. Collect before mutating.
  const legacyKeys: string[] = [];
  for (let index = 0; index < window.localStorage.length; index++) {
    const key = window.localStorage.key(index);
    if (key === OWNER_KEY_STORAGE || key?.startsWith(`${OWNER_KEY_STORAGE}@`)) {
      legacyKeys.push(key);
    }
  }
  for (const key of legacyKeys) window.localStorage.removeItem(key);
  const ownerKey = OWNER_FRAGMENT_PATTERN.exec(window.location.hash)?.[1];
  const value =
    ownerKey ??
    (legacyKey !== null && OWNER_KEY_PATTERN.test(legacyKey) && loadIdentity() === null
      ? legacyKey
      : null);
  bootstrapOwnerKey =
    value === null ? null : { storageKey, value, explicit: ownerKey !== undefined };
  if (ownerKey !== undefined) {
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${window.location.search}`,
    );
  }
}

function loadOwnerKey(): string | null {
  return bootstrapOwnerKey?.storageKey === credentialKey(OWNER_KEY_STORAGE)
    ? bootstrapOwnerKey.value
    : null;
}

interface IdentityGateProps {
  readonly children: (identity: StoredIdentity) => ReactNode;
}

/** Keeps every authenticated route behind the fragment bootstrap and first-person dialog. */
export function IdentityGate({ children }: IdentityGateProps) {
  const [identity, setIdentity] = useState<StoredIdentity | null>(() => loadIdentity());
  const [ownerKey, setOwnerKey] = useState<string | null>(() => loadOwnerKey());
  const [readmitting, setReadmitting] = useState(false);
  const [name, setName] = useState("");
  const [color, setColor] = useState<(typeof IDENTITY_COLORS)[number]>(IDENTITY_COLORS[3]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handoff = previewHandoffRequest();
  const identityOrigin = instanceOrigin();
  const explicitOwnerKey =
    bootstrapOwnerKey?.storageKey === credentialKey(OWNER_KEY_STORAGE) &&
    bootstrapOwnerKey.explicit;
  const invalidateIdentity = useCallback(() => {
    if (identity === null || instanceOrigin() !== identityOrigin) return;
    // Reads are not cross-tab compare-and-delete transactions. Leave the register
    // untouched: admission replaces it, and reload must still see the old credential
    // rather than treating a failed handoff as a first visit with an owner key.
    const replacement = loadIdentity();
    setReadmitting(true);
    setIdentity((current) => {
      // A queued callback only invalidates the in-memory bearer that started it.
      if (current?.token !== identity.token) return current;
      return replacement !== null && replacement.token !== identity.token ? replacement : null;
    });
  }, [identity, identityOrigin]);
  useEffect(() => {
    // A same-document owner link must pass through the same pre-render capture as
    // a full navigation, rather than leaving the secret in an inert SPA fragment.
    const openOwnerLink = () => {
      if (OWNER_FRAGMENT_PATTERN.test(window.location.hash)) window.location.reload();
    };
    window.addEventListener("hashchange", openOwnerLink);
    return () => window.removeEventListener("hashchange", openOwnerLink);
  }, []);
  useEffect(() => {
    if (identity === null) return;
    return onIdentityRejected(identity.token, invalidateIdentity);
  }, [identity, invalidateIdentity]);
  useEffect(() => {
    if (identity?.expiresInMs === undefined || identity.receivedAt === undefined) return;
    const timer = window.setTimeout(
      invalidateIdentity,
      Math.max(0, identity.expiresInMs - (Date.now() - identity.receivedAt)),
    );
    return () => window.clearTimeout(timer);
  }, [identity, invalidateIdentity]);

  // A key supplied for this page can recover an unusable identity. Cached owner
  // authority must never replace a rejected identity implicitly.
  const expired = identity !== null && identityExpired(identity);
  if (expired && !explicitOwnerKey) return <PreviewAdmission />;

  if (identity !== null && !expired) {
    if (handoff !== null) return <PreviewHandoff identity={identity} request={handoff} />;
    return children(identity);
  }

  if ((readmitting && !explicitOwnerKey) || ownerKey === null) return <PreviewAdmission />;

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const trimmedName = name.trim();
    if (trimmedName.length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const grant = await createPrincipal(ownerKey, { name: trimmedName, color });
      window.localStorage.setItem(credentialKey(IDENTITY_STORAGE), JSON.stringify(grant));
      bootstrapOwnerKey = null;
      setOwnerKey(null);
      setIdentity(grant);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Could not create your identity");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="gate-screen">
      <Cover className="gate-cover">
        <dialog className="identity-dialog" open aria-labelledby="identity-title">
          <form onSubmit={(event) => void submit(event)}>
            <p className="eyebrow">first visit</p>
            <h1 id="identity-title">Choose your identity</h1>
            <label className="field-label" htmlFor="identity-name">
              Name
            </label>
            <input
              id="identity-name"
              autoFocus
              maxLength={64}
              value={name}
              onChange={(event) => setName(event.currentTarget.value)}
              placeholder="How should collaborators see you?"
            />
            <fieldset className="color-fieldset">
              <legend>Color</legend>
              <div className="color-grid">
                {IDENTITY_COLORS.map((swatch) => (
                  <button
                    key={swatch}
                    className={swatch === color ? "color-swatch selected" : "color-swatch"}
                    type="button"
                    aria-label={`Use color ${swatch}`}
                    aria-pressed={swatch === color}
                    style={{ backgroundColor: swatch }}
                    onClick={() => setColor(swatch)}
                  />
                ))}
              </div>
            </fieldset>
            {/*
              Stays an inline form error rather than a notice, for two reasons that both
              hold. Structurally: the gate renders BEFORE the workspace, so there is no
              NoticeProvider above it — the notice layer is mounted inside the authenticated
              application. Substantively: this is field-level validation feedback about the
              submission the user is looking at, and it belongs beside that submit button,
              not in a corner of a screen with nothing else on it.
            */}
            {error === null ? null : <p className="form-error">{error}</p>}
            <button
              className="primary-button"
              data-testid="identity-enter"
              type="submit"
              disabled={submitting || name.trim() === ""}
            >
              {submitting ? "Creating identity…" : "Enter manifold"}
            </button>
          </form>
        </dialog>
      </Cover>
    </main>
  );
}
